/**
 * reconcileScope  (DA-2184)
 *
 * The question-level Mapper Scope pins every choice's READ-clause TARGET (HP attribute column /
 * AP appliance set). When the author changes that scope — or clears it — the choices must follow:
 * a clause left pinned to the attribute/appliance the author just moved away from is the
 * "previous one persists" bug, and clearing the scope should leave nothing still scoped.
 *
 * Changing the target also invalidates the operator/value that was chosen against the OLD target
 * (HP value codes are tag-bound to the attribute; AP fuel/size/attribute options are per-
 * appliance), so those are cleared too. Re-selecting the SAME target is a no-op — the guard
 * `scopeTargetChanged` lets the caller skip the rewrite (and the needless value reset) entirely.
 *
 * Pure + framework-free so it can be unit-tested without standing up the whole question form.
 */
import { AnswerChoice } from 'types/apis/surveyInterfaces'
import { MapperScopeConstraint } from './cascadeTypes'

/** Order-insensitive equality of two appliance-id sets (treats undefined as empty). */
export const sameIdSet = (a?: number[], b?: number[]): boolean => {
  const sa = new Set(a ?? [])
  const sb = new Set(b ?? [])
  if (sa.size !== sb.size) return false
  for (const id of sa) if (!sb.has(id)) return false
  return true
}

/** True when `next` targets a different attribute/appliance-set than `prev` (or clears it). */
export const scopeTargetChanged = (
  prev: MapperScopeConstraint | undefined,
  next: MapperScopeConstraint | undefined,
): boolean =>
  prev?.profileCode !== next?.profileCode ||
  (next?.profileCode === 'HP' && prev?.attributePath !== next.attributePath) ||
  (next?.profileCode === 'AP' && !sameIdSet(prev?.applianceIds, next.applianceIds)) ||
  (!!prev && !next)

/**
 * Retarget every choice's READ clauses to `next` (empty when the scope was cleared), clearing the
 * now-invalid value cascade. Choices whose clauses don't actually change keep their identity
 * (same object reference), and the array itself is returned unchanged when nothing moved — so a
 * caller can `setState` unconditionally without forcing a spurious re-render or diff.
 */
export const reconcileChoicesToScope = (
  choices: AnswerChoice[],
  next: MapperScopeConstraint | undefined,
): AnswerChoice[] => {
  const nextHpPath = next?.profileCode === 'HP' ? next.attributePath ?? '' : ''
  const scopedApIds = next?.profileCode === 'AP' ? next.applianceIds : undefined

  // Array.map always allocates, so it can't tell "every element unchanged" from a real edit.
  // Map clause-by-clause and keep the ORIGINAL array reference when nothing moved — that's what
  // lets an in-scope choice stay byte-identical (no spurious diff / re-render).
  const mapClauses = <T,>(arr: T[] | undefined, fn: (c: T) => T): T[] | undefined => {
    if (!arr?.length) return arr
    let changed = false
    const nextArr = arr.map((c) => {
      const nc = fn(c)
      if (nc !== c) changed = true
      return nc
    })
    return changed ? nextArr : arr
  }

  let anyChanged = false
  const reconciled = choices.map((choice) => {
    const hp = choice.hpMapperClauses
    const ap = choice.apMapperClauses

    // HP: retarget each READ clause to the new column (or empty on clear) and drop its value —
    // value codes are tag-bound to the attribute, so the old ones no longer apply.
    const nextHp = mapClauses(hp, (c) =>
      c.role !== 'READ'
        ? c
        : { ...c, attributePath: nextHpPath, operator: undefined, value: undefined, rangeStart: undefined, rangeEnd: undefined, isNull: undefined },
    )

    // AP: keep only appliance ids still in scope; when the id set actually shrinks, clear the
    // value cascade (per-appliance) and collapse a now-single group. Clearing the scope drops
    // every id so the picker unlocks empty.
    const nextAp = mapClauses(ap, (c) => {
      if (c.role !== 'READ') return c
      const keptIds = scopedApIds ? c.applianceIds.filter((id) => scopedApIds.includes(id)) : []
      if (sameIdSet(keptIds, c.applianceIds)) return c
      return {
        ...c,
        applianceIds: keptIds,
        groupOperator: keptIds.length > 1 ? c.groupOperator : undefined,
        value: undefined,
        rangeStart: undefined,
        rangeEnd: undefined,
        attributeKey: undefined,
        secondaryKey: undefined,
        secondaryValue: undefined,
      }
    })

    if (nextHp === hp && nextAp === ap) return choice
    anyChanged = true
    // Only touch the keys that actually changed so an unrelated absent key isn't introduced
    // as `undefined` (which the form's isEqual diff would otherwise read as a change).
    const updated = { ...choice }
    if (nextHp !== hp) updated.hpMapperClauses = nextHp
    if (nextAp !== ap) updated.apMapperClauses = nextAp
    return updated
  })

  return anyChanged ? reconciled : choices
}
